<template>
  <b-modal
    ref="layoutPreviewModal"
    ok-title="Import"
    cancel-title="Close"
    class="layout-preview-modal"
    size="lg"
  >
    <template #modal-title>
      Layout {{ layout ? `: ${layout.name}` : '' }}
    </template>

    <div
      v-if="layout"
      class="layout-preview-modal__container"
    >
      <div class="row w-100 h-100">
        <div class="col-6">
          <img
            :src="`/share?file=${layout.thumbnail}`"
            alt="layout-thumbnail"
          >
        </div>
        <div
          class="d-flex flex-column col-6"
          style="gap: 8px"
        >
          <div>
            <span>Templates</span>
          </div>
          <PreviewTemplate
            v-for="(template) in layout.instances"
            :key="template.instance_id"
            :template="template"
            :checked="selectedTemplates.some(instanceId => instanceId === template.instance_id)"
            :selected-layers="selectedLayers"
            @template-select="selectTemplate"
            @layer-select="selectLayer"
          />
        </div>
      </div>
    </div>
    <span
      v-else
      class="spinner-border spinner-border-sm"
      role="status"
      aria-hidden="true"
    />

    <template #modal-footer="{ cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        class="d-flex align-items-center"
        style="gap: 8px;"
        :disabled="!isAddingAvailable"
        @click="addLayers()"
      >
        <span
          v-if="isAdding"
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        />
        {{ isAdding ? 'Adding...' : 'Add' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import MediaAPI from "@frontend/services/api/media";
import JSZip from "jszip";
import PreviewTemplate from "@frontend/components/modules/layouts-page/modals/layout-preview/PreviewTemplate.vue";
import {getFilePath} from "@frontend/services/helpers";
import {LAYER_SUBTYPE} from "@frontend/components/modules/layouts-page/modals/layout-preview/constants";
import LayoutAPI from "@frontend/services/api/layout";

export default {
  components: {PreviewTemplate},
  data() {
    return {
      layout: null,
      selectedTemplates: [],
      selectedLayers: [],
      isAdding: false
    }
  },
  computed: {
    isAddingAvailable() {
      return !this.isAdding && this.layout && (this.selectedTemplates.length > 0 || this.selectedLayers.length > 0);
    },
  },
  methods: {
    async show(layoutId) {
      this.$refs.layoutPreviewModal.show();

      if (this.layout?.id !== layoutId) {
        this.reset();
        await this.fetchLayout(layoutId)
      }
    },
    hide() {
      this.$refs.layoutPreviewModal.hide();
    },
    async fetchLayout(layoutId) {
      const { data } = await LayoutAPI.getPreviews(0, layoutId);
      this.layout = { id: layoutId, ...data };
    },
    selectTemplate(instanceId) {
      const index = this.selectedTemplates.indexOf(instanceId);
      if (index > -1) {
        this.selectedTemplates.splice(index, 1);
      } else {
        // Temporarily only one template can be selected
        this.selectedTemplates = [instanceId];
        // Temporarily only template OR layer can be selected
        this.selectedLayers = [];
      }
    },
    selectLayer(layer) {
      const layerIndex = this.selectedLayers.findIndex(_layer => _layer.id === layer.id);

      if (layerIndex > -1) {
        this.selectedLayers.splice(layerIndex, 1);
      } else {
        // Temporarily only one layer can be selected
        this.selectedLayers = [layer];
        // Temporarily only template OR layer can be selected
        this.selectedTemplates = [];
      }
    },
    async addLayers() {
      this.isAdding = true;

      const imageFiles = [];
      const texts = [];

      if (this.selectedTemplates.length > 0) {
        const { data } = await MediaAPI.exportPSDFromLayout({
          dpi: 72,
          exclude_image: false,
          export_format: "jpg",
          hasCreateJob: false,
          include_psd: false,
          layout_id: this.layout.id,
          template_instance_id: this.selectedTemplates.join(','),
          use_PSD3: true,
          use_template_settings: false,
        });

        const zipResponse = await axios.get(data.url, { responseType: 'blob' });

        const zip = await JSZip.loadAsync(zipResponse.data);
        const files = Object.keys(zip.files);

        const images = await Promise.all(
            files.map(async (fileName) => {
              const fileBlob = await zip.files[fileName].async('blob');

              return {
                originalFile: new File([fileBlob], fileName, { type: fileBlob.type || 'image/jpeg' }),
                properties: {}
              };
            })
        );

        imageFiles.push(...images);
      }

      if (this.selectedLayers.length > 0) {
        const images = await Promise.all(
            this.selectedLayers
              .filter(layer => layer.properties.subType === LAYER_SUBTYPE.IMAGE)
              .map(async (layer) => {
                const filePath = getFilePath(layer.properties.src);
                const splitFilePath = filePath.split('/');
                const fileName = splitFilePath.pop();
                const { data: fileBlob } = await axios.get(layer.properties.src, { responseType: 'blob' });

                return {
                  originalFile: new File([fileBlob], fileName, { type: fileBlob.type }),
                  properties: layer.properties
                }
              })
        );
        const textLayers = this.selectedLayers
            .filter(layer => layer.properties.subType === LAYER_SUBTYPE.TEXT)
            .map(layer => layer.properties);

        imageFiles.push(...images);
        texts.push(...textLayers)
      }

      this.$emit('add-layers', { images: imageFiles, texts });

      this.isAdding = false;
    },
    reset() {
      this.layout = null;
      this.selectedTemplates = [];
      this.selectedLayers = [];
      this.isAdding = false;
    }
  }
}
</script>
